import { Story } from "../../types";

export const storyData: Story[] = [
    {
        id: "1",
        date: "January 22, 2024",
        title: "Podcast Recording Subscription Packages",
        type: "Audio Production",
        image: '/images/stories/story1.avif',
        content: [
            {
                type: "section",
                blocks: [
                    { type: "heading", level: 4, text: "Consistent, High-Quality Podcast Production—Made Easy" },
                    { type: "paragraph", text: "Whether you're launching your first show or scaling a growing series, our podcast subscription packages are designed to take the guesswork out of production. With flexible monthly plans, professional audio engineering, and dedicated support, you get everything you need to sound your best—every time you hit record." },
                    { type: "paragraph", text: "At the Broadcast Studio, we make it easy to stay on schedule, elevate your content, and grow your audience with confidence. Record in a comfortable, acoustically-treated space with expert guidance, fast turnaround, and studio-quality sound that sets your show apart." },
                    { type: "paragraph", style: 'b', text: "Choose the plan that fits your goals—and let’s build something great, one episode at a time." },
                ],
            },
            {
                type: "section",
                blocks: [
                    { type: "quote", text: "Professional-quality podcast recording, editing, and support—on your schedule." },
                ],
            },
            {
                type: "section",
                blocks: [
                    { type: "heading", level: 4, style: 'b', text: "Starter Plan – $300/month" },
                    { type: "paragraph", text: "Perfect for new podcasters or monthly shows." },
                    {
                        type: "list",
                            items: [
                                {text: "1 sessions/month (up to 90 minutes each)", bold: true},
                                { text: "Professional in-studio recording with an engineer" },
                                { text: "Light audio cleanup (EQ, volume leveling, noise reduction)"},
                                { text: "Intro/outro music integration (user-provided)"},
                                { text: "48-hour turnaround"},
                                { text: "1 revision per episode"}
                            ],
                    },
                    { type: "paragraph", style: 'b', text: "Add-ons:" },
                    {
                        type: "list",
                            items: [
                                { text: "Additional sessions: $125 each"},
                                { text: "Full mixing/editing: +$75/episode"}
                            ],
                    },
                    { type: "heading", level: 4, style: 'b', text: "Standard Plan – $550/month" },
                    { type: "paragraph", text: "Ideal for biweekly podcasters who want polished production." },
                    {
                        type: "list",
                        items: [
                            {text: "2 sessions/month (up to 90 minutes each)", bold: true},
                            { text: "Studio + engineer + producer support"},
                            { text: "Full editing, sound sweetening & pacing adjustment"},
                            { text: "Intro/outro music and ads integration"},
                            { text: "Social media teaser clip (up to 60 sec)"},
                            { text: "Priority 48-hour turnaround"},
                            { text: "2 revisions per episode"},
                            { text: "Bonus: 15% off any additional services (transcription, graphics, etc.)"}
                        ],
                    },
                    { type: "heading",  level: 4, style: 'b', text: "Pro Plan – $950/month" },
                    { type: "paragraph", text: "For weekly shows or brands building a podcast series." },
                    {
                        type: "list",
                        items: [
                            {text: "4 sessions/month (up to 90 minutes each)", bold: true},
                            { text: "Dedicated producer support and engineering"},
                            { text: "Full editing and mastering"},
                            { text: "Episode planning consults (1 hr/month)"},
                            { text: "Custom intro/outro creation (one-time service)"},
                            { text: "Social media clips (2 per episode)"},
                            { text: "Episode distribution support"},
                            { text: "24-hour priority turnaround"},
                            { text: "Unlimited minor revisions"},
                            { text: "Bonus: 25% off branded assets (cover art, audiograms, trailers)"}
                        ],
                    },
                    { type: "heading", level: 4, style: 'b', text: "Optional Add-Ons (All Plans):" },
                    {
                        type: "list",
                        items: [
                            { text: "Remote Guest Recording Support: +$50/session"},
                            { text: "Transcription Services: $1.25/min"},
                            { text: "Audiogram/Social Clips: $50 each"},
                            { text: "Additional Editing Time: $50/hr"}
                        ],
                    },
                ],
            }
        ],
    },
    {
        id: "2",
        date: "August 24, 2024",
        title: "Voiceover Services for Local Businesses",
        type: "Design",
        image: "/images/stories/story2.avif",
        content: [
            {
                type: "section",
                blocks: [
                    { type: "heading", level: 4, style: 'b', text: "Polished audio that makes your message stand out." },
                    { type: "paragraph", text: "From radio ads to phone greetings, your business deserves to sound as professional as it looks. Our voiceover services help small businesses, nonprofits, and local organizations deliver clean, confident messaging—recorded with clarity and precision in our studio."},
                    { type: "paragraph", text: "Whether you bring your own voice or need help finding one, we’ll guide you through the process, record in our acoustically treated space, and provide high-quality audio files ready for use across platforms like radio, YouTube, social media, or in-store playback"},
                ],
            },
            {
                type: "section",
                blocks: [
                    { type: "quote", text: "When people hear your brand, make sure it sounds like you mean it." },
                ],
            },
            {
                type: "section",
                blocks: [
                    { type: "heading", level: 4, text: "Up to 5 Minutes of Content – $150" },
                    {
                        type: "list",
                        items: [
                            { text: "Studio session with engineer" },
                            { text: "Voiceover recording and post-mix"},
                            { text: "Final delivery in WAV and MP3 formats"},
                            { text: "Ideal for phone menus, short ads, or in-store announcements"}
                        ],
                    },
                    { type: "heading", level: 4, text: "Up to 15 Minutes of Content – $250" },
                    {
                        type: "list",
                        items: [
                            { text: "Includes longer-form scripts, multiple takes if needed"},
                            { text: "Final, mixed audio ready for radio, video, or digital platforms"},
                        ],
                    },
                    { type: "heading", level: 4, text: "Optional Add-ons" },
                    {
                        type: "list",
                        items: [
                            { text: "Script Consultation or Edits – $50"},
                            { text: "Alternate file formats / additional export versions – $25"},
                            { text: "Add royalty-free background music – $35"},
                            { text: "Combine with video editing or social content – Custom quote"}
                        ],
                    },
                    { type: "paragraph", text: "This service is perfect for:" },
                    {
                        type: "list",
                        items: [
                            { text: "Local banks, realtors, and car dealerships"},
                            { text: "Schools, churches, and public service campaigns"},
                            { text: "Nonprofits and healthcare providers"},
                            { text: "Retailers and restaurants needing branded announcements or hold messaging"}
                        ],
                    },
                    { type: "paragraph", style: "b", text: "Clear voice. Clean sound. Quick turnaround." },
                    { type: "paragraph", text: "Let us help your message come through loud and clear." },
                    {
                        type: 'button',
                        text: '[Request a session]',
                        url: 'https://outlook.office365.com/book/BroadcastStudioBookings@broadcaststudio.com/?ismsaljsauthenabled=true',
                        color: 'secondary',
                        target: '_blank'
                    },
                    {
                        type: 'button',
                        text: '[Get a quote]',
                        url: 'mailto:info@broadcaststudio.com',
                        color: 'secondary',
                        target: '_blank'
                    },
                ],
            },
        ],
    },
    {
        id: "3",
        date: "April 05, 2024",
        title: "Mixing & Mastering Services",
        type: "Audio Engineering & Production",
        image: '/images/stories/story3.avif',
        content: [
            {
                type: "section",
                blocks: [
                    {
                        type: "heading",
                        level: 4,
                        text: "Mixing & Mastering Services"
                    },
                    {
                        type: "paragraph",
                        style: 'b',
                        text: "From raw tracks to radio-ready—your sound, fully realized.",
                    },
                    {
                        type: "paragraph",
                        text: "At the Broadcast Studio, we specialize in professional mixing and mastering services that bring clarity, depth, and polish to your audio. Whether you're recording a podcast, audiobook, music project, or branded content, our experienced engineers ensure your final product sounds exceptional on every platform."
                    },
                    {
                        type: "paragraph",
                        text: "Led by Clay Jones, our head of audio engineering with over two decades of industry experience, we bring a deep understanding of tone, space, and emotion to every mix. We balance levels, enhance dynamics, apply EQ and effects, and master to broadcast or streaming specifications—so your sound is consistent, competitive, and impactful."
                    },
                    {
                        type: "paragraph",
                        text: "Our mixing and mastering services are perfect for:"
                    },
                    {
                        type: "list",
                        items: [
                            { text: "Podcasts & Voiceovers"},
                            { text: "Albums & Singles"},
                            { text: "Audiobooks"},
                            { text: "Film, Video & Branded Content"},
                        ]
                    },
                    {
                        type: "paragraph",
                        style: 'b',
                        text: "Let’s make it sound incredible."
                    }
                ]
            },
            {
                type: "section",
                blocks: [
                    {
                        type: "quote",
                        text: "You bring the voice, the vision, the vibe—we’ll handle the finish."
                    },
                ],
            },
            {
                type: "section",
                blocks: [
                    {
                        type: "heading",
                        level: 4,
                        text: "Podcast Mixing & Mastering"
                    },
                    {
                        type: "list",
                        items: [
                            { text: "Single Episode (up to 60 minutes) – $175", bold: true },
                            "Includes level balancing, EQ, noise reduction, intro/outro and ad insertion, and mastering for podcast platforms",
                            { text: "Mini-Episode (under 30 minutes) – $125", bold: true },
                            { text: "Rush Turnaround (under 24 hours) – +$75", bold: true },
                            
                        ]
                    },
                    {
                        type: "paragraph",
                        style: 'b',
                        text: "Bundle Discount:"
                    },
                    {
                        type: "list",
                        items: [
                            { text: "4-Episode Package – $625 (Save $125)"},
                        ]
                    },
                    {
                        type: "heading",
                        level: 4,
                        text: "Music Mixing & Mastering"
                    },
                    {
                        type: "paragraph",
                        text: "Ideal for singles, EPs, and full-length albums."
                    },
                    {
                        type: "list",
                        items: [
                            { text: "Single Song – Mixing Only – $200–$300", bold: true },
                            "(Pricing varies based on track count and complexity)",
                            { text: "Single Song – Mastering Only – $100", bold: true },
                            { text: "Mixing + Mastering Combo – $275–$375", bold: true }
                        ]
                    },
                    {
                        type: "paragraph",
                        style: 'b',
                        text: "EP and Album Rates:"
                    },
                    {
                        type: "list",
                        items: [
                            { text: "EP (3–5 tracks) – Starting at $950" },
                            { text: "Full Album (6–12 tracks) – Custom quote (Average: $1,800–$2,500)" },
                        ]
                    },
                    { type: "paragraph", text: "Includes one round of revisions per track. Additional edits are billed at $50/hour."},
                    {
                        type: "heading",
                        level: 4,
                        text: "Audiobook Post-Production"
                    },
                    {
                        type: "list",
                        items: [
                            { text: "Mixing & Mastering (per finished hour) – $125", bold: true },
                            "Includes noise reduction, pacing and tone adjustment, and mastering to ACX/Findaway Voices standards",
                        ]
                    },
                    { type: "paragraph", text: "Discounted rates available for projects over 4 hours."},
                    {
                        type: "heading",
                        level: 4,
                        text: "Voiceover & Branded Content"
                    },
                    {
                        type: "paragraph",
                        text: "For marketing, explainers, corporate videos, animation, and more."
                    },
                    {
                        type: "list",
                        items: [
                            { text: "Up to 5 minutes – $150" },
                            { text: "5 to 15 minutes – $250" },
                            { text: "Full package pricing available for multi-video or campaign-based projects"}
                        ]
                    },
                    {
                        type: "heading",
                        level: 4,
                        text: "Add-On Services"
                    },
                    {
                        type: "list",
                        items: [
                            { text: "Stem Delivery – $25 per song or episode" },
                            { text: "Alternate Mix Versions (e.g., instrumental, no voiceover) – $35 each" },
                            { text: "Session File Delivery – $50" },
                            { text: "Additional Revisions – $50/hour"}
                        ]
                    },
                ],
            },
        ],
    },
    {
        id: "4",
        date: "July 08, 2024",
        title: "Audiobook Recording & Production",
        type: "Audiobook Production",
        image: '/images/stories/story4.avif',
        content: [
            {
                type: 'section',
                blocks: [
                    { type: "paragraph", style: 'b', text: "From page to polished performance—your book, brought to life." },
                    { type: "paragraph", text: "At the Broadcast Studio, we offer full-service audiobook production tailored to meet the needs of publishers, authors, and content creators. Whether you’re producing a debut memoir, a bestselling title, or an educational work, our experienced team ensures high-quality narration, editing, and mastering that meets industry standards." },
                    { type: "paragraph", text: "Led by head engineer Clay Jones—who brings over 20 years of expertise in voice recording, mixing, and mastering—we provide a quiet, comfortable recording environment with professional guidance every step of the way. We deliver broadcast-quality audio that meets ACX, Findaway Voices, and other distribution requirements, with seamless session management, direction (if needed), and post-production polish." },
                    { type: "paragraph", text: "We’re here to support storytellers at every stage—from seasoned publishers to first-time authors. Let’s turn your manuscript into an immersive listening experience." },
                ]
            },
             {
                type: 'section',
                blocks: [
                    { type: "quote", text: "Superior online services that answer all client requirements" },
                ]
            },
             {
                type: 'section',
                blocks: [
                    { type: "heading", level: 4, text: "Base Rates" },
                    { type: "paragraph", style: 'b', text: "Narration Recording with Engineer (In-Studio)" },
                    { 
                        type: "list",
                        items: [
                            { text: "$125 per studio hour (includes setup, recording, and engineer support)" },
                        ]
                    },
                    { type: "paragraph", style: "b", text: "Full Production (Recording + Editing + Mastering)"},
                    {
                        type: "list",
                        items: [
                            { text: "$250 per finished hour of audio (PFH)" },
                            { text: "Includes:", noStyle: true },
                            { text: "In-studio narration recording",  inset: true},
                            { text: "Editing for clarity, pacing, and flow", inset: true},
                            { text: "Noise reduction and mastering to meet ACX/Findaway standards", inset: true},
                            { text: "Final delivery of mastered files", inset: true},
                        ],
                    },
                    { type: "heading", level: 4, text: "Optional Services"},
                    {
                        type: "list",
                        items: [
                            { text: "Director for Narration Session – $75/hour", bold: true },
                            { text: "Remote Recording Support (via Source-Connect, Riverside, etc.) – $50/session", bold: true },
                            { text: "Proofing & Corrections (Optional QC pass by studio) – $50/hour", bold: true },
                            { text: "Pickups/Retakes – $75 per session or $25 per edit (if under 10 minutes)", bold: true },
                            { text: "File Splitting/Chapter Tagging – Included for most projects; custom setups quoted separately", bold: true },
                            { text: "Rush Delivery (<5 business days) – +20% surcharge", bold: true },
                        ],
                    },
                    { type: "heading", level: 4, text: "Discounts & Packages"},
                    {
                        type: "list",
                        items: [
                            { text: "Projects over 5 PFH: 10% discount on total" },
                            { text: "Projects over 10 PFH: Custom quote available" },
                            { text: "Author-narrated projects: Consultation included at no charge" },
                        ],
                    },
                ]
            },
        ],
    },
    {
        id: "5",
        date: "June 12, 2024",
        title: "Video Podcast & Interview Recording",
        type: "Video Podcast & Interview Recording",
        image: "/images/stories/story5.avif",
        content: [
            {
                type: 'section',
                blocks: [
                    { type: 'paragraph', style: 'b', text: "High-quality video content that matches the strength of your message."},
                    { type: 'paragraph', text: "Whether you’re hosting a podcast, launching a new series, or capturing expert interviews, professional video makes all the difference. At The Broadcast Studio, we offer single- and multi-camera setups designed to elevate your content with clear visuals, crisp audio, and minimal hassle."},
                    { type: 'paragraph', text: "Our team supports you from setup to final edit, so you can focus on your message—not the tech. We help you walk away with content you’re proud to share—across YouTube, social, websites, and streaming platforms."},
                ],
            },
            {
                type: "section",
                blocks: [
                    { type: "quote", text: "When the content is strong, the production should rise to meet it."},
                ],
            },
            {
                type: "section",
                blocks: [
                    { type: "heading", level: 4, text: "Single-Camera Session – $375/session" },
                    {
                        type: "list",
                        items: [
                            { text: "Up to 60 minutes in-studio recording" },
                            { text: "Professional camera + lighting setup" },
                            { text: "Studio-quality audio capture" },
                            { text: "Basic edit with your logo, intro/outro, and color correction" },
                            { text: "Final file optimized for web/social"}
                        ],
                    },
                    { type: "heading", level: 4, text: "Multi-Camera Session – $750/session"},
                    {
                        type: "list",
                        items: [
                            { text: "Up to 60 minutes in-studio with 2–3 camera angles" },
                            { text: "Live or post-session camera switching" },
                            { text: "Studio-quality audio + synced tracks" },
                            { text: "Basic post-edit with your branding and layout" },
                            { text: "Final video optimized for publishing"}
                        ],
                    },
                    { type: "heading", level: 4, text: "Custom Packages"},
                    { type: "paragraph", text: "For mini-series, ongoing podcast seasons, or branded content campaigns, we offer discounted custom packages that include recurring recording sessions, editing, and content strategy support."},
                    { type: "paragraph", text: "Let’s build something powerful—and make sure it looks as good as it sounds."},
                    {
                        type: 'button',
                        text: '[Book a session]',
                        url: 'https://outlook.office365.com/book/BroadcastStudioBookings@broadcaststudio.com/?ismsaljsauthenabled=true',
                        color: 'secondary',
                        target: '_blank'
                    },
                    {
                        type: 'button',
                        text: '[Request a custom quote]',
                        url: 'mailto:info@broadcaststudio.com',
                        color: 'secondary',
                        target: '_blank'
                    },
                ],
            },
        ],
    },
    {
        id: "6",
        date: "March 28, 2024",
        title: "Community & Creator Services",
        type: "Audio Recording, Production & Community",
        image: '/images/stories/story6.avif',
        content: [
            {
                type: "section",
                blocks: [
                    { type: "paragraph", style: "b", text: "Affordable, professional tools to amplify your story, brand, or legacy."},
                    { type: "paragraph", text: "The Broadcast Studio isn’t just for big productions—it’s a home for everyday voices, small businesses, and creative thinkers. Whether you're preserving family stories, recording a message for your audience, or leveling up your brand’s presence online, our team offers high-quality, approachable services that help you sound—and look—your best." },
                    { type: "paragraph", text: "We offer a range of accessible services designed for individuals, families, entrepreneurs, educators, and community leaders. All sessions include guidance from our experienced team and the use of our acoustically treated space, with optional editing and post-production support." },
                ],
            },
            {
                type: "section",
                blocks: [
                    { type: "quote", text: "Every voice has value. Whether you’re sharing your legacy, your brand, or your next big idea—we’re here to help you tell it well."}
                ],
            },
            {
                type: "section",
                blocks: [
                    { type: "paragraph", style: "b", text: "Legacy & Oral History Recordings" },
                    { type: "paragraph", text: "Capture your story, your voice, and your family’s history in a permanent and professional format."},
                    {
                        type: "list",
                        items: [
                            { text: "Single Session (up to 90 minutes) – $250", bold: true},
                            "Includes engineer-led recording, light editing, and delivery of digital audio file",
                            { text: "Legacy Package – $750", bold: true },
                            "Includes 2–3 sessions, professional editing, musical intro/outro, and final mastered file (digital and optional USB/CD keepsake)",
                            { text: "Add-ons: Transcription – $75 | Additional edits – $50/hr" },
                        ],
                    },
                    { type: "paragraph", style: "b", text: "Voiceover Recording for Small Business" },
                    { type: "paragraph", text: "Create sharp, clear audio for your radio ad, voicemail system, in-store announcements, or YouTube ads."},
                    {
                        type: "list",
                        items: [
                            { text: "Up to 5 minutes of content – $150", bold: true},
                            { text: "Up to 15 minutes – $250", bold: true },
                            { text: "Includes engineer support, post-mix, and delivery of ready-to-use file"},
                            { text: "Add-ons: Script help – $50 | Multiple file exports – $25" },
                        ],
                    },
                    { type: "paragraph", style: "b", text: "Creator Access: Studio Membership" },
                    { type: "paragraph", text: "Flexible, monthly access for content creators, influencers, and storytellers. Ideal for those creating podcasts, YouTube videos, digital courses, or reels."},
                    {
                        type: "list",
                        items: [
                            { text: "Lite Plan – $250/month", bold: true},
                            { text: "4 hours of studio access/month", inset: true },
                            { text: "Engineer-assisted setup + light editing", inset: true },
                            { text: "Pro Plan – $500/month", bold: true },
                            { text: "8 hours of studio time/month", inset: true},
                            { text: "Editing support + basic post-production", inset: true},
                            { text: "Priority scheduling", inset: true},
                            { text: "Additional hours: $60/hour (member rate)" },
                            { text: "Month-to-month or discounted 3-month commitment" },
                        ],
                    },
                    { type: "paragraph", style: "b", text: "Video Podcast or Interview Recording" },
                    { type: "paragraph", text: "Make a polished visual impression with multi-cam or single-camera video podcast content."},
                    {
                        type: "list",
                        items: [
                            { text: "Single-Camera Shoot – $375/session (up to 60 minutes)", bold: true},
                            "Includes basic edit with your logo and intro/outro",
                            { text: "Multi-Camera Setup – $750/session", bold: true },
                            "Includes synced video + audio, basic switching, and edit",
                            { text: "Custom packages available for mini-series or ongoing episodes" },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: "7",
        date: "May 18, 2024",
        title: "Legacy & Oral History Recordings",
        type: "Audio Recording & Production",
        image: '/images/stories/story7.avif',
        content: [
            {
                type: "section",
                blocks: [
                    { type: "paragraph", style: "b", text: "Capture the stories that shaped you—told in your own voice." },
                    { type: "paragraph", text: "Our oral history sessions are designed to preserve life stories, reflections, and memories in a professional audio format—so that families, communities, and future generations can hear directly from the people who lived them."},
                    { type: "paragraph", text: "Whether it’s a conversation with a loved one, a personal life narrative, or a family archive project, we create a calm, respectful space to record and edit your story with care. Each session includes an experienced engineer to guide the process, ensure high-quality sound, and deliver a polished, listenable final file."},
                    { type: "paragraph", text: "This service is ideal for families, educators, cultural organizations, estate planning, and personal legacy work. We welcome all voices—no performance needed, just the truth as you tell it."},
                ],
            },
             {
                type: "section",
                blocks: [
                    { type: "quote", text: "We don’t preserve stories because they’re perfect. We preserve them because they’re real." },
                ],
            },
             {
                type: "section",
                blocks: [
                    { type: "paragraph", style: "b", text: "Legacy Session – $250" },
                    {
                        type: "list",
                        items: [
                            { text: "Up to 90 minutes in-studio recording with engineer" },
                            { text: "Light editing for clarity and pacing" },
                            { text: "Final mastered audio file (MP3 and WAV)" },
                        ],
                    },
                    { type: "paragraph", style: "b", text: "Legacy Package – $750" },
                    {
                        type: "list",
                        items: [
                            { text: "Up to 3 recording sessions (can be done over time)" },
                            { text: "Professional editing with optional intro music and structure" },
                            { text: "Chapter markers or segments (if desired)" },
                            { text: "Final mastered file in multiple formats (digital + optional keepsake USB or CD)" },
                            { text: "Optional: includes prep session and guided storytelling prompts" },
                        ],
                    },
                    { type: "paragraph", style: "b", text: "Legacy Session – $250" },
                    {
                        type: "list",
                        items: [
                            { text: "Printed transcript – $75" },
                            { text: "Additional editing – $50/hour" },
                            { text: "Extra recording time – $100/hour" },
                            { text: "Remote family interview (via Zoom, recorded at studio) – $125" },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: "8",
        date: "February 14, 2024",
        title: "Relaxing sounds of nature",
        type: "Music",
        image: '/images/stories/story8.avif',
        content: [
            {
                type: 'section',
                blocks: [
                    { type: "paragraph", text: "The soothing undulations of untouched expanses, be it the unrelenting cadence of the ocean, the gentle thrum of a light drizzle, or the orchestrated melodies of bird songs, have remained a significant element of our existence. The perpetual nexus between humankind and the surroundings exerts a substantial impression. Harmonizing with Nature's harmonies refines our nighttime rest."},
                    { type: "paragraph", text: "We acknowledge the soothing impact of Earth's melodious strains the lyrical song of a nightingale or the isolated hum of a raindrop brings peace, quieting our resonant heart rhythms. Our listening senses profoundly connect with our convictions, experiences, and sense of comfort. As we descend into slumber, the slightest sound can startle us into alertness, whether it be the slam of a car door, the creaking of a swinging gate, or the persistent hum of city life. Those who relish the sound banquet of chirping birds, or the sea's rhythm experienced expedited sleep onset and improved overall health." },
                ],
            },
            {
                type: 'section',
                blocks: [
                    { type: "quote", text: "Our listening senses profoundly connect with our convictions, experiences, and sense of comfort."}
                ],
            },
            {
                type: 'section',
                blocks: [
                    { type: "paragraph", text: "Nonetheless, in modern times burdened with persistent noise and electronic interference, harnessing moments of peacefulness has transformed into a sought-after luxury. Arranged harmonies for tranquility instill focus, particularly when they are wordless, as lyrics might lead us toward mental activity instead of inducing tranquility."},
                ],
            },
        ],
    },
];
